<script lang="ts">
  import { createEventDispatcher } from 'svelte';
  import { importAdminTickets } from '../../lib/convexApi';

  export let open = false;
  export let eventId: string | null = null;
  export let eventName: string | null = null;
  export let eventCode: string | null = null;
  export let existingGuestKeys: string[] = [];

  const dispatch = createEventDispatcher<{
    close: void;
    imported: { imported: number; skipped: number };
  }>();

  let rawText = '';
  let uniqueNames: string[] = [];
  let skippedCount = 0;
  let error: string | null = null;
  let isImporting = false;

  let existingGuestKeySignature = '';

  let initialized = false;

  $: if (open) {
    if (!initialized) {
      resetState();
      initialized = true;
    }
  } else if (initialized) {
    initialized = false;
  }

  $: existingGuestKeySignature = open ? existingGuestKeys.join('|') : '';
  $: if (open) {
    void existingGuestKeySignature;
    parseInput(rawText);
  }

  $: uniqueReady = uniqueNames.length;

  function resetState() {
    rawText = '';
    uniqueNames = [];
    skippedCount = 0;
    error = null;
  }

  function normalizeDisplayName(value: string): string | null {
    const trimmed = value.trim();
    if (!trimmed) return null;
    return trimmed.replace(/\s+/g, ' ');
  }

  function buildDedupeKey(value: string | null): string | null {
    if (!value) return null;
    try {
      const normalized = value.normalize('NFD').replace(/\p{M}/gu, '');
      return normalized.toLowerCase();
    } catch (err) {
      return value.toLowerCase();
    }
  }

  function parseInput(text: string) {
    const existingKeys = new Set(existingGuestKeys);
    const localSeen = new Set<string>();
    const nextUnique: string[] = [];
    let nextSkipped = 0;

    const lines = text.split(/\r?\n/);
    for (const line of lines) {
      const normalized = normalizeDisplayName(line);
      if (!normalized) {
        continue;
      }
      const key = buildDedupeKey(normalized);
      if (!key) {
        continue;
      }
      if (localSeen.has(key) || existingKeys.has(key)) {
        nextSkipped += 1;
        continue;
      }
      localSeen.add(key);
      nextUnique.push(normalized);
    }

    uniqueNames = nextUnique;
    skippedCount = nextSkipped;
  }

  function handleInput(event: Event) {
    const value = (event.currentTarget as HTMLTextAreaElement).value;
    rawText = value;
    error = null;
  }

  function close() {
    if (isImporting) return;
    dispatch('close');
  }

  async function handleImport() {
    if (!eventId) {
      error = 'Choose an event before importing guests.';
      return;
    }
    if (uniqueNames.length === 0) {
      error = 'Add at least one unique guest name.';
      return;
    }

    isImporting = true;
    error = null;
    try {
      await importAdminTickets(eventId, uniqueNames);
      dispatch('imported', { imported: uniqueNames.length, skipped: skippedCount });
    } catch (err) {
      error = err instanceof Error ? err.message : 'Failed to import guests';
    } finally {
      isImporting = false;
    }
  }
</script>

{#if open}
  <dialog class="guest-import" open>
    <div class="modal-card">
      <header>
        <div>
          <h3>Bulk import guests</h3>
          {#if eventName || eventCode}
            <p class="muted">{eventName ?? 'Guestlist'} · {eventCode ?? eventId}</p>
          {:else if eventId}
            <p class="muted">Event {eventId}</p>
          {/if}
        </div>
        <button type="button" class="ghost" on:click={close} disabled={isImporting}>Close</button>
      </header>

      <label class="textarea">
        <span>Paste names (one per line)</span>
        <textarea
          rows="8"
          bind:value={rawText}
          placeholder="Jane Doe\nMs. Rosa Álvarez\nLee Chen"
          on:input={handleInput}
        ></textarea>
      </label>

      <p class="summary">
        Unique ready: <strong>{uniqueReady}</strong>
        <span class="spacer">·</span>
        Skipped duplicates: <strong>{skippedCount}</strong>
      </p>
      <p class="helper">Deduplicated by normalized name (ignores case, spacing, accents).</p>

      {#if error}
        <p class="error">{error}</p>
      {/if}

      <footer>
        <button type="button" class="ghost" on:click={close} disabled={isImporting}>Cancel</button>
        <button
          type="button"
          class="primary"
          on:click={handleImport}
          disabled={isImporting || uniqueNames.length === 0}
        >
          {isImporting ? 'Importing…' : 'Import guests'}
        </button>
      </footer>
    </div>
  </dialog>
{/if}

<style>
  dialog.guest-import {
    position: fixed;
    inset: 0;
    margin: auto;
    background: rgba(7, 8, 12, 0.75);
    border: none;
    width: min(540px, 90vw);
    padding: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 20;
  }

  .modal-card {
    width: 100%;
    padding: var(--space-5);
    border-radius: var(--radius-xl);
    background: linear-gradient(180deg, rgba(16, 18, 26, 0.92), rgba(16, 18, 26, 0.85));
    border: 1px solid rgba(255, 255, 255, 0.08);
    display: flex;
    flex-direction: column;
    gap: var(--space-4);
  }

  header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: var(--space-3);
  }

  header h3 {
    margin: 0;
  }

  header .muted {
    margin: 0;
    font-size: 0.85rem;
    color: var(--color-text-muted);
  }

  .textarea {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
    font-size: 0.85rem;
  }

  textarea {
    border-radius: var(--radius-md);
    border: 1px solid transparent;
    background: rgba(255, 255, 255, 0.08);
    color: inherit;
    padding: 0.6rem 0.75rem;
    resize: vertical;
    min-height: 10rem;
  }

  .summary {
    margin: 0;
    display: flex;
    align-items: center;
    gap: 0.5rem;
    font-size: 0.9rem;
  }

  .summary strong {
    font-weight: 600;
  }

  .spacer {
    opacity: 0.5;
  }

  .helper {
    margin: 0;
    font-size: 0.8rem;
    color: var(--color-text-muted);
  }

  .error {
    margin: 0;
    color: #ff9a9a;
    font-size: 0.85rem;
  }

  footer {
    display: flex;
    justify-content: flex-end;
    gap: var(--space-3);
  }

  button {
    border: none;
    border-radius: var(--radius-lg);
    padding: 0.55rem 1rem;
    cursor: pointer;
    font-weight: 600;
  }

  button:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  button.primary {
    background: var(--color-accent, #6a5bff);
    color: white;
  }

  button.ghost {
    background: rgba(255, 255, 255, 0.1);
    color: inherit;
  }
</style>
